import { isValid, parse, format as formatFNS } from "date-fns";

/** Milliseconds per day. */
const DAY_MS = 86_400_000;

/**
 * Excel serial → calendar date, zone-independent (#7467).
 *
 * Serial 1 is 1899-12-31 and serial 60 is the phantom 1900-02-29, so the
 * arithmetic is day offsets from 1899-12-30 in UTC. Reading the UTC parts back
 * as a local-midnight Date gives the calendar date the cell meant, in every
 * zone. Converting through an instant (`new Date(serial * DAY_MS ...)`) and
 * formatting locally shifts the date west of UTC and made the same serial
 * render as Jan 1 on a US laptop and Jan 2 on the UTC build agent.
 */
const excelSerialToDate = (serial: number): Date => {
  // A fractional part is time-of-day (46023.75 = 6 pm on Jan 1), so floor it;
  // the epsilon (~0.1 s) absorbs float noise like 46022.9999999 from a JSON round-trip.
  const day = Math.floor(serial + 1e-6);
  const utc = new Date(Date.UTC(1899, 11, 30) + day * DAY_MS);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
};

const STRING_FORMATS = ["yyyy-MM-dd", "M/d/yy", "M/d/yyyy", "MM/dd/yy", "MM/dd/yyyy"];

export const getExcelDate = (dateInput: number | string, reqFormat: string): string => {
  let date: Date | undefined;

  if (typeof dateInput === "number") {
    date = excelSerialToDate(dateInput);
  } else {
    const input = dateInput.trim();
    // Calendar-date strings first. These are local dates, not instants:
    // `new Date("2026-01-01")` is UTC midnight and formats as Dec 31 west of UTC.
    for (const fmt of STRING_FORMATS) {
      const attempt = parse(input, fmt, new Date());
      if (isValid(attempt)) {
        date = attempt;
        break;
      }
    }
    // Anything else (full ISO timestamps, RFC 2822) is an instant; let Date read it.
    if (!date) date = new Date(input);
  }

  if (!date || !isValid(date)) {
    throw new Error(`Invalid date format: ${dateInput}`);
  }

  return formatFNS(date, reqFormat);
};
